import { zodResolver } from '@hookform/resolvers/zod';
import {
  ExperienceConfiguration,
  ExperienceConfigurationKeys,
  ExperienceConfigurationRequest,
  QuoteAndContractSettingsConfig,
  QuoteAndContractSettingsConfigWithKey,
  QuoteAndContractSettingsConfigWithKeySchema,
} from '@monetize/types/experience-config';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  useGetExperienceConfiguration,
  useUpdateExperienceConfiguration,
} from '../../../../../api/experienceConfigurationService';
import {
  MAccordion,
  MAccordionCustomButton,
  MBox,
  MPageLoader,
} from '../../../../../components/Monetize';
import { MSettingAccordionItem } from '../../../../../components/Monetize/MSettingAccordionItem';
import { useAccordionControl } from '../../../../../hooks';
import { useAsyncStatus } from '../../../../../hooks/useAsyncStatus';
import { useScrollToHighlightedSection } from '../../../../../hooks/useScrollToHighlightedSection';
import { getHighlightStyles } from '../../../../../hooks/useSettingsSectionHighlight';
import { useSettingsSectionHighlights } from '../../../../../hooks/useSettingsSectionHighlights';
import { SettingsSection } from '../../../components/SettingsSection';
import { SettingsToggle } from '../../../components/SettingsToggle';
import {
  QUOTE_SETTING_KEYS,
  getQuoteSettingsSectionKeys,
} from '../quoteSettingsConfig';
import {
  QuoteAndContractSettingsBooleanKeys,
  QuoteAndContractSettingsMetadata,
} from './QuoteExperienceConfigSettings/quote-settings-experience-config.utils';
import { ExperienceConfigRoleBasedSetting } from './QuoteExperienceConfigSettings/RoleBasedSetting';

export const quoteRateAndPriceSettingsOwnedBooleanKeys: QuoteAndContractSettingsBooleanKeys[] =
  [
    'accountSpecificRateAllowTierAdjustments',
    'accountSpecificRateAllowPriceAdjustments',
    'accountSpecificRateAllowPercentageAdjustments',
    'lockPriceModelUpdateOnQuote',
    'allowQuantityDropdown',
    'enablePriceTierLockEligible',
  ];

export const QuoteRateAndPriceSettings = () => {
  const sectionHighlight = useSettingsSectionHighlights(
    getQuoteSettingsSectionKeys(QUOTE_SETTING_KEYS.rateAndPriceSettings),
  );

  const { data, isLoading } = useGetExperienceConfiguration();

  const { mutateAsync: doUpdateExperienceConfiguration, isPending } =
    useUpdateExperienceConfiguration({
      meta: { showErrorToast: true },
      onSuccess: (updatedData: ExperienceConfiguration) => {
        reset({
          key: ExperienceConfigurationKeys.quoteAndContractSettings,
          configuration:
            updatedData.configuration as QuoteAndContractSettingsConfig,
        });
      },
    });

  const { control, reset, handleSubmit, setValue } =
    useForm<QuoteAndContractSettingsConfigWithKey>({
      mode: 'onChange',
      resolver: zodResolver(QuoteAndContractSettingsConfigWithKeySchema) as any,
      defaultValues: {
        key: ExperienceConfigurationKeys.quoteAndContractSettings,
        configuration: data?.quoteAndContractSettings || {},
      },
    });

  useEffect(() => {
    if (data) {
      reset({
        key: ExperienceConfigurationKeys.quoteAndContractSettings,
        configuration: data.quoteAndContractSettings,
      });
    }
  }, [data, reset]);

  useScrollToHighlightedSection(sectionHighlight, !!data && !isLoading);

  const onSubmit = async (formValues: ExperienceConfigurationRequest) => {
    await doUpdateExperienceConfiguration(formValues);
  };

  const [activeKey, setActiveKey] = useState<string | null>(null);
  const { status, track } = useAsyncStatus();

  const { accordionValue, handleAccordionChange, setAccordionValue } =
    useAccordionControl();

  useEffect(() => {
    if (Object.values(sectionHighlight).some(Boolean)) {
      setAccordionValue(['0']);
    }
  }, [sectionHighlight, setAccordionValue]);

  return (
    <MAccordion
      multiple
      data-testid="quote-price-settings"
      w="100%"
      value={accordionValue}
      onValueChange={({ value }) => handleAccordionChange(value)}
    >
      <MSettingAccordionItem
        value="0"
        id={QUOTE_SETTING_KEYS.rateAndPriceSettings}
        {...getHighlightStyles(
          sectionHighlight[QUOTE_SETTING_KEYS.rateAndPriceSettings],
        )}
        renderAccordionButton={({ isExpanded }) => (
          <MAccordionCustomButton
            isExpanded={isExpanded}
            label="Rate and Price Settings"
          />
        )}
        borderWidth="1px"
      >
        {data && !isLoading ? (
          <MBox mt={4}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <SettingsSection
                id="accountSpecificRateCreation"
                {...getHighlightStyles(
                  sectionHighlight['accountSpecificRateCreation'],
                )}
              >
                <ExperienceConfigRoleBasedSetting
                  metadataKey="accountSpecificRateCreation"
                  value={
                    data.quoteAndContractSettings.accountSpecificRateCreation
                  }
                  isLoading={isPending}
                  onSave={(newValue) => {
                    setValue(
                      'configuration.accountSpecificRateCreation',
                      newValue,
                    );
                    handleSubmit(onSubmit)();
                  }}
                />
              </SettingsSection>

              {quoteRateAndPriceSettingsOwnedBooleanKeys.map((key, i) => {
                const typedKey = key as QuoteAndContractSettingsBooleanKeys;
                const isInverse = typedKey === 'lockPriceModelUpdateOnQuote';
                const isHighlighted = !!sectionHighlight[typedKey];
                return (
                  <SettingsSection
                    key={key}
                    id={typedKey}
                    {...getHighlightStyles(isHighlighted)}
                    showDivider={
                      i < quoteRateAndPriceSettingsOwnedBooleanKeys.length - 1
                    }
                  >
                    <Controller
                      name={`configuration.${typedKey}` as const}
                      control={control}
                      render={({ field: { value, onChange, ...rest } }) => (
                        <SettingsToggle
                          heading={
                            QuoteAndContractSettingsMetadata[typedKey].label
                          }
                          subheading={
                            QuoteAndContractSettingsMetadata[typedKey]
                              .description
                          }
                          value={isInverse ? !value : !!value}
                          asyncStatus={activeKey === typedKey ? status : 'idle'}
                          onChange={(newValue) => {
                            setActiveKey(typedKey);
                            onChange(isInverse ? !newValue : newValue);
                            track(() => handleSubmit(onSubmit)());
                          }}
                          {...rest}
                        />
                      )}
                    />
                  </SettingsSection>
                );
              })}
            </form>
          </MBox>
        ) : (
          <MPageLoader />
        )}
      </MSettingAccordionItem>
    </MAccordion>
  );
};
